/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as ModalActionCreators from '@app/actions/ModalActionCreators';
import {acceptTerms} from '@app/actions/TermsAcceptanceActionCreators';
import * as Modal from '@app/components/modals/Modal';
import {Button} from '@app/components/uikit/button/Button';
import {Routes} from '@app/Routes';
import UserStore from '@app/stores/UserStore';
import markupStyles from '@app/styles/Markup.module.css';
import {PRIVACY_POLICY_LAST_UPDATED, TERMS_OF_SERVICE_LAST_UPDATED} from '@fluxer/constants/src/PolicyConstants';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback, useRef, useState} from 'react';

type UpdateKind = 'terms' | 'privacy' | 'both';

function getUpdateKind(termsAgreedAt: Date | null | undefined, privacyAgreedAt: Date | null | undefined): UpdateKind {
	const termsOutdated =
		TERMS_OF_SERVICE_LAST_UPDATED != null &&
		(!termsAgreedAt || termsAgreedAt.toISOString() < TERMS_OF_SERVICE_LAST_UPDATED);
	const privacyOutdated =
		PRIVACY_POLICY_LAST_UPDATED != null &&
		(!privacyAgreedAt || privacyAgreedAt.toISOString() < PRIVACY_POLICY_LAST_UPDATED);

	if (termsOutdated && privacyOutdated) return 'both';
	if (termsOutdated) return 'terms';
	return 'privacy';
}

export const TermsAcceptanceModal = observer(() => {
	const {t} = useLingui();
	const user = UserStore.currentUser;
	const [submitting, setSubmitting] = useState(false);
	const primaryRef = useRef<HTMLButtonElement | null>(null);

	const handleClose = useCallback(() => {
		ModalActionCreators.pop();
	}, []);

	const handleAccept = useCallback(async () => {
		setSubmitting(true);
		try {
			await acceptTerms();
			ModalActionCreators.pop();
		} finally {
			setSubmitting(false);
		}
	}, []);

	if (!user) return null;

	const kind = getUpdateKind(user.termsAgreedAt, user.privacyAgreedAt);

	const termsUrl = Routes.terms();
	const privacyUrl = Routes.privacy();

	return (
		<Modal.Root size="small" initialFocusRef={primaryRef} centered onClose={handleClose}>
			<Modal.Header title={t`Updated Policies`} onClose={handleClose} />
			<Modal.Content>
				<Modal.ContentLayout>
					<Modal.Description className={markupStyles.markup}>
						{kind === 'terms' && (
							<Trans>
								We've made significant changes to our{' '}
								<a href={termsUrl} target="_blank" rel="noopener noreferrer" className={markupStyles.link}>
									Terms of Service
								</a>
								. Please review it before continuing to use Fluxer.
							</Trans>
						)}
						{kind === 'privacy' && (
							<Trans>
								We've made significant changes to our{' '}
								<a href={privacyUrl} target="_blank" rel="noopener noreferrer" className={markupStyles.link}>
									Privacy Policy
								</a>
								. Please review it before continuing to use Fluxer.
							</Trans>
						)}
						{kind === 'both' && (
							<Trans>
								We've made significant changes to our{' '}
								<a href={termsUrl} target="_blank" rel="noopener noreferrer" className={markupStyles.link}>
									Terms of Service
								</a>{' '}
								and{' '}
								<a href={privacyUrl} target="_blank" rel="noopener noreferrer" className={markupStyles.link}>
									Privacy Policy
								</a>
								. Please review them before continuing to use Fluxer.
							</Trans>
						)}
					</Modal.Description>
					<Modal.Description className={markupStyles.markup} style={{marginTop: 12, opacity: 0.7, fontSize: '0.85em'}}>
						<Trans>By continuing to use Fluxer, you agree to the updated policies.</Trans>
					</Modal.Description>
				</Modal.ContentLayout>
			</Modal.Content>
			<Modal.Footer>
				<Button onClick={handleClose} variant="secondary">
					{t`Dismiss`}
				</Button>
				<Button onClick={handleAccept} submitting={submitting} variant="primary" ref={primaryRef}>
					{kind === 'terms' && t`I Agree to the Updated Terms`}
					{kind === 'privacy' && t`I Agree to the Updated Privacy Policy`}
					{kind === 'both' && t`I Agree to the Updated Policies`}
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});
